import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes/routeConfig';
import { INSTITUTIONS, fetchInstitutionHoldings } from '../../utils/parse13f';
import { fetchAllStockFundamentals } from '../../utils/stockFundamentals';
import { handleWechatLinkClick } from '../../utils/wechatLink';
import BrandMark from '../../components/BrandMark';
import MarketPriceChart from '../../components/MarketPriceChart';
import useUserStore from '../../store/userStore';
import GoldTrendRadar from '../../features/goldTrend/GoldTrendRadar';
import { fallbackHeatmapData, fallbackMarketData } from './marketData';
import useBinanceTicker from './useBinanceTicker';
import './market.css';

const tabs = [
  { id: 'crypto', label: '加密市场' },
  { id: 'gold', label: '黄金' },
  { id: 'stock', label: '美股' },
];

const TAB_BY_PATH = {
  [ROUTES.market]: 'crypto',
  [ROUTES.marketGold]: 'gold',
  [ROUTES.marketStock]: 'stock',
};
const PATH_BY_TAB = {
  crypto: ROUTES.market,
  gold: ROUTES.marketGold,
  stock: ROUTES.marketStock,
};

function pathToTab(pathname) {
  return TAB_BY_PATH[pathname] || 'crypto';
}

const tools = [
  { title: 'K线形态匹配', desc: '把历史形态转成交易前检查清单。', path: ROUTES.marketToolsPatternMatch },
  { title: '砸盘侦探', desc: '观察大额转账和异常卖压信号。', path: ROUTES.marketToolsDumpDetective },
  { title: '支撑阻力', desc: '快速标注关键价位和复盘区域。', path: ROUTES.marketToolsSupportResistance },
];

const stockQuoteSymbols = ['NVDA', 'MSTR', 'COIN', 'QQQ', 'TSLA', 'AAPL', 'AMD'];
const stockFundTickers = ['AAPL', 'NVDA', 'AMZN', 'MSFT', 'TSLA', 'META'];
const cryptoChartAssets = [
  { symbol: 'BTCUSDT', label: 'BTC' },
  { symbol: 'ETHUSDT', label: 'ETH' },
  { symbol: 'SOLUSDT', label: 'SOL' },
  { symbol: 'PAXGUSDT', label: 'PAXG' },
];
const emptyMarketData = {};
const emptyHeatmapData = [];
const troyOunceGrams = 31.1034768;
const goldApiCandidates = ['/api/gold/benchmark'];

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const fmtUsd = (value, digits = 0) => {
  const number = toFiniteNumber(value);
  if (number === null) return '$--';
  return `$${number.toLocaleString(undefined, { maximumFractionDigits: digits })}`;
};

const fmtCompact = (value) => {
  const number = toFiniteNumber(value);
  if (number === null || number === 0) return '--';
  if (number >= 1e12) return `$${(number / 1e12).toFixed(2)}T`;
  if (number >= 1e9) return `$${(number / 1e9).toFixed(0)}B`;
  if (number >= 1e6) return `$${(number / 1e6).toFixed(0)}M`;
  return fmtUsd(number, 0);
};

const pct = (value) => {
  const number = toFiniteNumber(value);
  if (number === null) return '--';
  return `${number >= 0 ? '+' : ''}${number.toFixed(2)}%`;
};

const fmtCny = (value, digits = 2) => {
  const number = toFiniteNumber(value);
  if (number === null) return '¥--';
  return `¥${number.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

const fmtGoldGram = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? '--' : number.toFixed(2);
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const isPresentNumber = (value) => toFiniteNumber(value) !== null;
const todayText = () => new Date().toISOString().slice(0, 10);

function makeGoldBenchmarkFallback() {
  return {
    date: todayText(),
    updatedAt: '--',
    quoteTime: '--',
    source: '数据源暂不可用',
    xauUsd: null,
    usdCnh: null,
    internationalCnyPerGram: null,
    domesticGoldCnyPerGram: null,
    icbcGoldCnyPerGram: null,
    abcGoldCnyPerGram: null,
    domesticSource: 'not_available',
    domesticPremiumCnyPerGram: null,
    domesticPremiumPct: null,
  };
}

function nowDisplayText(timestamp = Date.now()) {
  return new Date(timestamp).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function normalizeGoldBenchmark(payload) {
  // 兼容旧接口和第一阶段公开参考数据接口。
  const snapshot = payload?.snapshot || payload?.data || payload?.benchmark || payload || {};
  const quotes = snapshot.quotes || {};
  const intl = snapshot.international || {};
  const domestic = snapshot.domestic || {};
  const fallback = makeGoldBenchmarkFallback();

  const xauUsd = toNumber(
    intl.usdPerOunce ?? quotes.xauUsd?.price ?? snapshot.xauUsd ?? snapshot.xau_usd ?? snapshot.spotUsdPerOz,
    null
  );
  const usdCnh = toNumber(
    quotes.usdCnh?.price ?? snapshot.usdCnh ?? snapshot.usd_cnh ?? snapshot.fxRate,
    null
  );
  const internationalCnyPerGram = toNumber(
    intl.cnyPerGram ?? snapshot.internationalCnyPerGram ?? snapshot.international_cny_per_gram,
    Number.isFinite(xauUsd) && Number.isFinite(usdCnh) ? xauUsd * usdCnh / troyOunceGrams : null
  );
  const domesticGoldCnyPerGram = toNumber(
    domestic.priceCnyPerGram ?? domestic.icbcCnyPerGram ?? snapshot.domesticGoldCnyPerGram ?? snapshot.icbcGoldCnyPerGram,
    null
  );

  return {
    date: snapshot.snapshotDate || snapshot.date || snapshot.tradeDate || fallback.date,
    updatedAt: snapshot.capturedAt || snapshot.updatedAt || snapshot.updated_at || fallback.updatedAt,
    quoteTime: quotes.xauUsd?.time || snapshot.quoteTime || snapshot.quote_time || fallback.quoteTime,
    source: snapshot.sourceMode === 'realtime' ? '实时参考' : (snapshot.source || snapshot.dataSource || fallback.source),
    xauUsd,
    usdCnh,
    internationalCnyPerGram,
    domesticGoldCnyPerGram,
    icbcGoldCnyPerGram: domesticGoldCnyPerGram,
    abcGoldCnyPerGram: toNumber(domestic.abcCnyPerGram ?? snapshot.abcGoldCnyPerGram ?? snapshot.abc_gold_cny_per_gram, null),
    domesticSource: domesticGoldCnyPerGram === null ? 'not_available' : (snapshot.domesticSource || snapshot.domestic_source || domestic.source || 'domestic_reference'),
    domesticPremiumCnyPerGram: snapshot.domesticPremiumCnyPerGram ?? snapshot.domestic_premium_cny_per_gram ?? null,
    domesticPremiumPct: snapshot.domesticPremiumPct ?? snapshot.domestic_premium_pct ?? null,
  };
}

async function fetchGoldBenchmark() {
  if (typeof fetch !== 'function') return makeGoldBenchmarkFallback();

  for (const endpoint of goldApiCandidates) {
    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        console.warn(`[MarketPage] Gold benchmark endpoint unavailable: ${endpoint} (${response.status})`);
        continue;
      }
      const payload = await response.json();
      return normalizeGoldBenchmark(payload);
    } catch (error) {
      console.warn(`[MarketPage] Gold benchmark endpoint failed: ${endpoint}`, error);
    }
  }

  console.error('[MarketPage] Gold benchmark data unavailable. In local development, run `npm run dev:api` before `npm start` so /api requests are proxied to Cloudflare Pages Functions.');
  return makeGoldBenchmarkFallback();
}

const heatColor = (change) => {
  if (change >= 2) return 'rgba(18, 150, 94, 0.42)';
  if (change >= 0) return 'rgba(18, 150, 94, 0.24)';
  if (change >= -2) return 'rgba(207, 79, 69, 0.28)';
  return 'rgba(207, 79, 69, 0.46)';
};

const mergeCoinSnapshot = (coins) => {
  const next = {};
  coins.forEach((coin) => {
    if (!coin?.id) return;
    next[coin.id] = {
      ...coin,
    };
  });
  return next;
};

async function fetchMarketSnapshot() {
  if (typeof fetch !== 'function') return null;
  const response = await fetch('/api/market/snapshot');
  if (!response.ok) throw new Error(`Market snapshot ${response.status}`);
  const payload = await response.json();
  return {
    coins: Array.isArray(payload?.coins) ? payload.coins : [],
    source: payload?.source || 'CoinGecko markets',
    updatedAt: payload?.updatedAt || null,
  };
}

async function fetchFearGreed() {
  if (typeof fetch !== 'function') return null;
  const response = await fetch('/api/market/fear-greed');
  if (!response.ok) throw new Error(`Fear greed ${response.status}`);
  const payload = await response.json();
  return payload?.data || null;
}

function normalizeHistoryPayload(payload) {
  const rows = payload?.candles || payload?.data || payload?.prices || [];
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    time: row.time,
    value: Number(row.value ?? row.close ?? row.price),
  })).filter((point) => point.time && Number.isFinite(point.value));
}

async function fetchGoldHistory() {
  if (typeof fetch !== 'function') return { points: [], source: 'Yahoo Finance chart', symbol: 'GC=F' };
  const response = await fetch('/api/gold/history?range=6mo&interval=1d');
  if (!response.ok) throw new Error(`Gold history ${response.status}`);
  const payload = await response.json();
  return {
    points: normalizeHistoryPayload(payload),
    source: payload?.source || 'Yahoo Finance chart',
    symbol: payload?.symbol || 'GC=F',
  };
}

async function fetchCryptoHistory(symbol) {
  if (typeof fetch !== 'function') return { points: [], source: 'Binance public klines', symbol };
  const response = await fetch(`/api/market/history?symbol=${symbol}&interval=1d&limit=180`);
  if (!response.ok) throw new Error(`Market history ${response.status}`);
  const payload = await response.json();
  return {
    points: normalizeHistoryPayload(payload),
    source: payload?.source || 'Binance public klines',
    symbol: payload?.symbol || symbol,
  };
}

async function fetchStockQuotes() {
  if (typeof fetch !== 'function') return {};
  const response = await fetch(`/api/fundamentals?symbols=${encodeURIComponent(stockQuoteSymbols.join(','))}`);
  if (!response.ok) throw new Error(`Fundamentals quote ${response.status}`);
  const payload = await response.json();
  const quotes = {};
  Object.entries(payload?.data || {}).forEach(([symbol, quote]) => {
    quotes[symbol] = quote;
  });
  return quotes;
}

function MetricCard({ label, value, change, tone, loading }) {
  return (
    <div className="market-metric-card">
      <span>{label}</span>
      <strong>{loading ? '加载中' : value}</strong>
      {change && <em className={tone || (String(change).startsWith('-') ? 'down' : 'up')}>{change}</em>}
    </div>
  );
}

export default function MarketPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, userInfo, logout } = useUserStore();
  const [activeTab, setActiveTab] = useState(() => pathToTab(location.pathname));
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const mainRef = useRef(null);

  // 路径变化时同步板块（浏览器前进/后退、外部深链）
  useEffect(() => {
    setActiveTab(pathToTab(location.pathname));
    setActiveSection('');
  }, [location.pathname]);

  // 兼容旧 ?tab= 链接 → 重定向到子路径
  useEffect(() => {
    const qtab = new URLSearchParams(location.search).get('tab');
    if (qtab && PATH_BY_TAB[qtab] && PATH_BY_TAB[qtab] !== location.pathname) {
      navigate(PATH_BY_TAB[qtab], { replace: true });
    }
  }, [location, navigate]);

  const handleTabChange = useCallback((tabId) => {
    setMobileMenuOpen(false);
    navigate(PATH_BY_TAB[tabId] || ROUTES.market, { replace: true });
  }, [navigate]);
  const [marketData, setMarketData] = useState(emptyMarketData);
  const [heatmapData, setHeatmapData] = useState(emptyHeatmapData);
  const [marketLoading, setMarketLoading] = useState(true);
  const [marketSource, setMarketSource] = useState('加载实时行情中');
  const [marketUpdatedAt, setMarketUpdatedAt] = useState('--');
  const [fearGreed, setFearGreed] = useState(null);
  const [cryptoChartSymbol, setCryptoChartSymbol] = useState('BTCUSDT');
  const [cryptoChartData, setCryptoChartData] = useState([]);
  const [cryptoChartSource, setCryptoChartSource] = useState('BTCUSDT · Binance public klines');
  const [cryptoChartLoading, setCryptoChartLoading] = useState(true);
  const [cryptoChartError, setCryptoChartError] = useState('');
  const [stockQuotes, setStockQuotes] = useState({});
  const [, setStockFunds] = useState(null);
  const [, setStockFundsLoading] = useState(true);
  const [, setHoldingsPreview] = useState([]);
  const [, setHoldingsLoading] = useState(true);
  const [goldBenchmark, setGoldBenchmark] = useState(() => makeGoldBenchmarkFallback());
  const [goldBenchmarkLoading, setGoldBenchmarkLoading] = useState(true);
  const [goldChartData, setGoldChartData] = useState([]);
  const [goldChartSource, setGoldChartSource] = useState('GC=F · 6个月 · 日线');
  const [goldChartLoading, setGoldChartLoading] = useState(true);
  const [goldChartError, setGoldChartError] = useState('');
  const [goldGrams, setGoldGrams] = useState('10');

  const handleLiveTicker = useCallback((update) => {
    setMarketData((current) => {
      const coin = current[update.coinId];
      if (!coin) return current;
      return {
        ...current,
        [update.coinId]: {
          ...coin,
          current_price: update.current_price,
          price_change_percentage_24h: update.price_change_percentage_24h,
          high_24h: update.high_24h,
          low_24h: update.low_24h,
        },
      };
    });
    setHeatmapData((current) => current.map((coin) => (
      coin.id === update.coinId
        ? {
          ...coin,
          current_price: update.current_price,
          price_change_percentage_24h: update.price_change_percentage_24h,
          high_24h: update.high_24h,
          low_24h: update.low_24h,
        }
        : coin
    )));
    setMarketSource('Binance live ticker');
    setMarketUpdatedAt(nowDisplayText(update.updatedAt));
  }, []);

  useBinanceTicker({
    enabled: activeTab === 'crypto',
    onTicker: handleLiveTicker,
  });

  useEffect(() => {
    let cancelled = false;
    if (activeTab !== 'crypto') {
      return undefined;
    }

    async function loadCrypto() {
      setMarketLoading(true);
      const [coinsResult, fngResult] = await Promise.allSettled([
        fetchMarketSnapshot(),
        fetchFearGreed(),
      ]);

      if (cancelled) return;

      if (coinsResult.status === 'fulfilled' && Array.isArray(coinsResult.value?.coins) && coinsResult.value.coins.length) {
        setMarketData(mergeCoinSnapshot(coinsResult.value.coins));
        setHeatmapData(coinsResult.value.coins);
        setMarketSource(coinsResult.value.source || 'CoinGecko markets');
        setMarketUpdatedAt(nowDisplayText());
      } else {
        setMarketSource('数据源暂不可用');
        setMarketData(fallbackMarketData);
        setHeatmapData(fallbackHeatmapData);
        setMarketUpdatedAt('--');
      }

      if (fngResult.status === 'fulfilled' && fngResult.value) {
        setFearGreed(fngResult.value);
      }

      setMarketLoading(false);
    }

    loadCrypto().catch(() => {
      if (!cancelled) setMarketLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    if (activeTab !== 'crypto') {
      return undefined;
    }

    async function loadCryptoChart() {
      setCryptoChartLoading(true);
      setCryptoChartError('');
      try {
        const next = await fetchCryptoHistory(cryptoChartSymbol);
        if (!cancelled) {
          setCryptoChartData(next.points);
          setCryptoChartSource(`${next.symbol} · ${next.source}`);
        }
      } catch (error) {
        if (!cancelled) {
          setCryptoChartData([]);
          setCryptoChartSource(`${cryptoChartSymbol} · Binance public klines`);
          setCryptoChartError('加密行情图表暂时不可用');
        }
      } finally {
        if (!cancelled) setCryptoChartLoading(false);
      }
    }

    loadCryptoChart();
    return () => {
      cancelled = true;
    };
  }, [activeTab, cryptoChartSymbol]);

  useEffect(() => {
    let cancelled = false;
    if (activeTab !== 'gold') {
      return undefined;
    }

    async function loadGold() {
      setGoldBenchmarkLoading(true);
      const next = await fetchGoldBenchmark();

      if (!cancelled) {
        setGoldBenchmark(next);
        setGoldBenchmarkLoading(false);
      }
    }

    loadGold().catch(() => {
      if (!cancelled) {
        setGoldBenchmark(makeGoldBenchmarkFallback());
        setGoldBenchmarkLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    if (activeTab !== 'gold') {
      return undefined;
    }

    async function loadGoldChart() {
      setGoldChartLoading(true);
      setGoldChartError('');
      try {
        const next = await fetchGoldHistory();
        if (!cancelled) {
          setGoldChartData(next.points);
          setGoldChartSource(`${next.symbol} · ${next.source}`);
        }
      } catch (error) {
        if (!cancelled) {
          setGoldChartData([]);
          setGoldChartSource('GC=F · 6个月 · 日线');
          setGoldChartError('黄金行情图表暂时不可用');
        }
      } finally {
        if (!cancelled) setGoldChartLoading(false);
      }
    }

    loadGoldChart();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    if (process.env.REACT_APP_STOCK_MODULE_LIVE !== 'true' || activeTab !== 'stock') {
      setStockFundsLoading(false);
      setHoldingsLoading(false);
      return undefined;
    }

    async function loadStocks() {
      setStockFundsLoading(true);

      const [quotesResult, fundamentalsResult, holdingsResult] = await Promise.allSettled([
        fetchStockQuotes(),
        fetchAllStockFundamentals(stockFundTickers),
        Promise.allSettled(
          INSTITUTIONS.slice(0, 4).map(async (institution) => ({
            institution,
            data: await fetchInstitutionHoldings(institution),
          }))
        ),
      ]);

      if (cancelled) return;

      if (quotesResult.status === 'fulfilled') {
        setStockQuotes(quotesResult.value || {});
      }

      if (fundamentalsResult.status === 'fulfilled') {
        setStockFunds(fundamentalsResult.value || null);
      }

      if (holdingsResult.status === 'fulfilled') {
        setHoldingsPreview(
          holdingsResult.value
            .filter((result) => result.status === 'fulfilled' && result.value?.data)
            .map((result) => result.value)
        );
      }

      setStockFundsLoading(false);
      setHoldingsLoading(false);
    }

    loadStocks().catch(() => {
      if (!cancelled) {
        setStockFundsLoading(false);
        setHoldingsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  // ── IntersectionObserver：滚动时自动高亮侧栏 ──
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          setActiveSection(visible[0].target.id);
        }
      },
      { root: main, rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    );

    main.querySelectorAll('section[id]').forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [activeTab]);

  const scrollToSection = useCallback((id) => {
    const el = mainRef.current?.querySelector(`#${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  }, []);

  const marketRows = useMemo(() => (
    Object.values(marketData).map((coin) => ({
      ...coin,
      change: coin.price_change_percentage_24h || 0,
    }))
  ), [marketData]);

  const btc = marketData.bitcoin || {};
  const eth = marketData.ethereum || {};
  const gold = marketData['pax-gold'] || {};
  const goldInternational = toFiniteNumber(goldBenchmark.internationalCnyPerGram);
  const goldDomestic = toFiniteNumber(goldBenchmark.icbcGoldCnyPerGram);
  const hasInternationalGold = goldInternational !== null;
  const hasDomesticGold = goldDomestic !== null;
  const goldPremium = hasDomesticGold && hasInternationalGold ? goldDomestic - goldInternational : null;
  const goldPremiumPct = hasDomesticGold && goldInternational ? (goldPremium / goldInternational) * 100 : null;
  const goldGramNumber = toNumber(goldGrams, 0);
  const goldInternationalTotal = hasInternationalGold ? goldInternational * goldGramNumber : null;
  const goldDomesticTotal = hasDomesticGold ? goldDomestic * goldGramNumber : null;
  const goldSpreadTotal = hasDomesticGold && hasInternationalGold ? goldDomesticTotal - goldInternationalTotal : null;
  const fngValue = fearGreed?.value ? Number(fearGreed.value) : null;
  const fngLabel = fearGreed?.value_classification || '数据源不可用';
  const fngDisplayValue = Number.isFinite(fngValue) ? fngValue : '--';
  const fngGaugeValue = Number.isFinite(fngValue) ? fngValue : 50;
  const fngTone = Number.isFinite(fngValue) ? (fngValue <= 25 ? 'fear' : fngValue >= 75 ? 'greed' : 'neutral') : 'neutral';
  const displayUsername = userInfo?.username || userInfo?.email || '账户';
  const shortUsername = displayUsername.length > 12 ? `${displayUsername.slice(0, 10)}...` : displayUsername;
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setAccountOpen(false);
    closeMobileMenu();
    navigate(ROUTES.home);
  };

  const refreshGoldBenchmark = async () => {
    setGoldBenchmarkLoading(true);
    try {
      setGoldBenchmark(await fetchGoldBenchmark());
    } finally {
      setGoldBenchmarkLoading(false);
    }
  };

  return (
    <main className="market-page market-terminal">
      <nav className="market-local-tabs" aria-label="市场资产分类">
        <div className="market-mobile-head">
          <button className="market-brand-lockup" type="button" onClick={() => navigate(ROUTES.home)} aria-label="返回首页">
            <span>GHOST</span>
            <BrandMark className="market-brand-mark" alt="GHOST" />
          </button>
          <button
            type="button"
            className="market-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-expanded={mobileMenuOpen}
            aria-controls="market-secondary-actions"
            aria-label="打开市场菜单"
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
        </div>
        <div className="market-tab-group">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? 'active' : ''}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div
          id="market-secondary-actions"
          className={`market-top-actions ${mobileMenuOpen ? 'market-top-actions--open' : 'market-top-actions--collapsed'}`}
        >
          <button type="button" className="market-top-link" onClick={() => { closeMobileMenu(); navigate(ROUTES.academy); }}>财富学院</button>
          <button type="button" className="market-top-link" onClick={() => { closeMobileMenu(); navigate(ROUTES.rebate); }}>交易所注册</button>
          {isLoggedIn ? (
            <div className="market-account">
              <button
                type="button"
                className="market-account-btn"
                onClick={() => setAccountOpen((open) => !open)}
                onBlur={() => setTimeout(() => setAccountOpen(false), 150)}
                aria-expanded={accountOpen}
                aria-haspopup="menu"
              >
                <span className="market-account-avatar">{String(shortUsername).slice(0, 1).toUpperCase()}</span>
                <span className="market-account-name">{shortUsername}</span>
                <span className="market-account-caret" aria-hidden="true">{accountOpen ? '⌃' : '⌄'}</span>
              </button>
              {accountOpen && (
                <div className="market-account-menu" role="menu">
                  <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { setAccountOpen(false); closeMobileMenu(); navigate(ROUTES.profile); }}>
                    个人资料
                  </button>
                  <button type="button" role="menuitem" className="danger" onMouseDown={(event) => event.preventDefault()} onClick={handleLogout}>
                    退出登录
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="market-auth-actions">
              <button type="button" onClick={() => { closeMobileMenu(); navigate(ROUTES.login); }}>登录</button>
              <button type="button" className="primary" onClick={() => { closeMobileMenu(); navigate(ROUTES.register); }}>注册</button>
            </div>
          )}
        </div>
      </nav>

      <div className="market-shell">
        <aside className="market-sidebar" aria-label="市场研究院导航">
          {activeTab === 'crypto' && (
            <>
              <div className="market-sidebar-label">市场</div>
              <button className={activeSection === 'crypto-overview' ? 'active' : ''} type="button" onClick={() => scrollToSection('crypto-overview')}>市场总览</button>
              <button className={activeSection === 'crypto-chart' ? 'active' : ''} type="button" onClick={() => scrollToSection('crypto-chart')}>价格走势</button>
              <button className={activeSection === 'crypto-radar' ? 'active' : ''} type="button" onClick={() => scrollToSection('crypto-radar')}>市场雷达</button>
              <button type="button" onClick={() => navigate(ROUTES.marketHeatmap)}>热力图</button>
              <div className="market-sidebar-label">工具</div>
              {tools.map((tool) => <button type="button" key={tool.path} onClick={() => navigate(tool.path)}>{tool.title}</button>)}
            </>
          )}
          {activeTab === 'gold' && (
            <>
              <div className="market-sidebar-label">行情</div>
              <button className={activeSection === 'gold-overview' ? 'active' : ''} type="button" onClick={() => scrollToSection('gold-overview')}>金价总览</button>
              <button className={activeSection === 'gold-chart' ? 'active' : ''} type="button" onClick={() => scrollToSection('gold-chart')}>价格走势</button>
              <button className={activeSection === 'gold-trend' ? 'active' : ''} type="button" onClick={() => scrollToSection('gold-trend')}>趋势雷达</button>
              <div className="market-sidebar-label">工具</div>
              <button className={activeSection === 'gold-converter' ? 'active' : ''} type="button" onClick={() => scrollToSection('gold-converter')}>内外盘换算</button>
            </>
          )}
          {activeTab === 'stock' && (
            <>
              <div className="market-sidebar-label">市场</div>
              <button className={activeSection === 'stock-overview' ? 'active' : ''} type="button" onClick={() => scrollToSection('stock-overview')}>美股总览</button>
            </>
          )}
          <div className="market-sidebar-label">社群</div>
          <button type="button" onClick={() => navigate(ROUTES.community)}>
            加入社群 →
          </button>
        </aside>

        <section className="market-main" ref={mainRef}>
          {activeTab === 'crypto' && (
            <div className="market-panel">
              <header className="market-headline" id="crypto-overview">
                <div>
                  <span>Bitcoin / USD</span>
                  <h1>{fmtUsd(btc.current_price)}</h1>
                  <em className={btc.price_change_percentage_24h >= 0 ? 'up' : 'down'}>{pct(btc.price_change_percentage_24h)}</em>
                  <div className="market-gold-meta market-source-meta">
                    <span className="market-gold-meta-source">
                      数据来源：{marketSource}
                    </span>
                    <span className="market-gold-meta-time">
                      更新于 {marketUpdatedAt}
                    </span>
                  </div>
                </div>
              </header>

              <div className="market-brief">
                <MetricCard label="BTC 市值" value={fmtCompact(btc.market_cap)} change="CoinGecko" tone="neutral" loading={marketLoading} />
                <MetricCard label="恐惧贪婪指数" value={String(fngDisplayValue)} change={fngLabel} tone={fngTone === 'fear' ? 'down' : fngTone === 'greed' ? 'up' : 'neutral'} />
                <MetricCard label="ETH / USD" value={fmtUsd(eth.current_price)} change={pct(eth.price_change_percentage_24h)} loading={marketLoading} />
                <MetricCard label="BTC 24h 高低" value={`${fmtUsd(btc.low_24h)} - ${fmtUsd(btc.high_24h)}`} change="实时区间" tone="neutral" loading={marketLoading} />
              </div>

              <div className="market-dashboard-grid">
                <section className="market-card market-chart-card" id="crypto-chart">
                  <div className="market-chart-head">
                    <div className="market-section-title"><span /> 加密价格走势</div>
                    <div>
                      <div className="market-chart-tabs" aria-label="加密图表资产">
                        {cryptoChartAssets.map((asset) => (
                          <button
                            type="button"
                            key={asset.symbol}
                            className={cryptoChartSymbol === asset.symbol ? 'active' : ''}
                            onClick={() => setCryptoChartSymbol(asset.symbol)}
                          >
                            {asset.label}
                          </button>
                        ))}
                      </div>
                      <div className="market-chart-source">{cryptoChartSource}</div>
                    </div>
                  </div>
                  <MarketPriceChart
                    data={cryptoChartData}
                    tone="crypto"
                    loading={cryptoChartLoading}
                    error={cryptoChartError}
                    label={`${cryptoChartSymbol} 价格走势`}
                  />
                </section>
                <section className="market-card market-gauge" data-fng={fngTone}>
                  <span>恐惧贪婪指数</span>
                  <strong>{fngDisplayValue}</strong>
                  <div><i style={{ left: `${Math.min(98, Math.max(2, fngGaugeValue))}%` }} /></div>
                  <em>{fngLabel}</em>
                </section>
              </div>

              <section className="market-card" id="crypto-radar">
                <div className="market-section-title"><span /> 市场雷达</div>
                <div className="market-table">
                  {marketRows.slice(0, 6).map((coin, index) => (
                    <button type="button" key={coin.id} onClick={() => navigate(`/crypto/${coin.id}`)}>
                      <span>{index + 1}</span>
                      <strong>{coin.name}</strong>
                      <em>{fmtUsd(coin.current_price, coin.current_price < 1 ? 4 : 2)}</em>
                      <b className={coin.change >= 0 ? 'up' : 'down'}>{pct(coin.change)}</b>
                      <small>{fmtCompact(coin.market_cap)}</small>
                    </button>
                  ))}
                </div>
              </section>

              <section className="market-card" id="crypto-heatmap">
                <div className="market-section-title"><span /> 热力图</div>
                <div className="market-heatmap-grid">
                  {heatmapData.map((asset) => (
                    <button
                      type="button"
                      key={asset.id}
                      style={{ backgroundColor: heatColor(asset.price_change_percentage_24h) }}
                      onClick={() => navigate(`/crypto/${asset.id}`)}
                    >
                      <strong>{asset.symbol.toUpperCase()}</strong>
                      <span>{pct(asset.price_change_percentage_24h)}</span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="market-card" id="crypto-tools">
                <div className="market-section-title"><span /> 工具箱</div>
                <div className="market-tools-grid">
                  {tools.map((tool) => (
                    <button type="button" key={tool.path} onClick={() => navigate(tool.path)}>
                      <strong>{tool.title}</strong>
                      <span>{tool.desc}</span>
                    </button>
                  ))}
                </div>
              </section>
            </div>
          )}

          {activeTab === 'gold' && (
            <div className="market-panel">
              {/* 1. 金价行情 Hero */}
              <header className="market-headline market-gold-hero" id="gold-overview">
                <div>
                  <span>GC=F · 国际黄金期货参考</span>
                  <h1>{fmtUsd(goldBenchmark.xauUsd, 2)}</h1>
                  <div className="market-gold-meta">
                    <span className="market-gold-meta-source">
                      美元/金衡盎司
                    </span>
                    <span className="market-gold-meta-source">
                      数据来源：{goldBenchmark.source}
                    </span>
                    <span className="market-gold-meta-time">
                      更新于 {goldBenchmark.updatedAt}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="market-gold-refresh"
                  onClick={refreshGoldBenchmark}
                  disabled={goldBenchmarkLoading}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, opacity: goldBenchmarkLoading ? 0.4 : 0.7 }}>
                    <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0115-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 01-15 6.7L3 16" />
                  </svg>
                  {goldBenchmarkLoading ? '刷新中…' : '刷新参考价'}
                </button>
              </header>

              <div className="market-brief">
                <MetricCard label="国内参考金" value={fmtGoldGram(goldDomestic)} change={hasDomesticGold ? '元/克' : '未接入'} tone="neutral" loading={goldBenchmarkLoading} />
                <MetricCard label="国际折算价" value={hasInternationalGold ? `${goldInternational.toFixed(2)}` : '--'} change={hasInternationalGold ? '元/克' : '数据源不可用'} tone="neutral" loading={goldBenchmarkLoading} />
                <MetricCard label="国内溢价" value={hasDomesticGold && hasInternationalGold ? `${goldPremium >= 0 ? '+' : ''}${goldPremium.toFixed(2)}` : '--'} change={hasDomesticGold && hasInternationalGold ? pct(goldPremiumPct) : '等待国内价'} tone={goldPremium >= 0 ? 'up' : 'down'} />
                <MetricCard label="USDCNH" value={isPresentNumber(goldBenchmark.usdCnh) ? Number(goldBenchmark.usdCnh).toFixed(4) : '--'} change="汇率参考" tone="neutral" loading={goldBenchmarkLoading} />
              </div>

              <section className="market-card market-chart-card" id="gold-chart">
                <div className="market-chart-head">
                  <div className="market-section-title"><span /> 黄金价格走势</div>
                  <div className="market-chart-source">{goldChartSource}</div>
                </div>
                <MarketPriceChart
                  data={goldChartData}
                  tone="gold"
                  loading={goldChartLoading}
                  error={goldChartError}
                  label="黄金价格走势"
                />
              </section>

              {/* 交易入口 */}
              <a
                className="market-gold-trade-banner"
                href="https://one.exnessonelink.com/a/4sgfej491a"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWechatLinkClick}
              >
                <div className="market-gold-trade-banner-brand">
                  <img src="/logos/exness.svg" alt="Exness" />
                </div>
                <div className="market-gold-trade-banner-text">
                  <strong>立即开始交易黄金</strong>
                  <span>通过 Exness 交易平台，低点差、快速执行</span>
                </div>
                <span className="market-gold-trade-banner-cta">前往交易 →</span>
              </a>

              {/* 2. 趋势雷达 */}
              <section className="market-card" id="gold-trend" style={{ marginTop: '1.15rem' }}>
                <div className="market-section-title"><span /> 黄金4小时趋势雷达</div>
                <p style={{ color: 'var(--market-soft)', fontSize: '0.78rem', marginBottom: '1rem' }}>
                  基于维加斯通道 EMA144 / 169 / 576 / 676 判断黄金中期趋势
                </p>
                <GoldTrendRadar displayPrice={goldBenchmark.xauUsd} />
              </section>

              {/* 3. 内外盘换算器 */}
              <section className="market-card" id="gold-converter" style={{ marginTop: '1.15rem' }}>
                <div className="market-section-title"><span /> 黄金内外盘换算器</div>
                <p style={{ color: 'var(--market-soft)', fontSize: '0.78rem', marginBottom: '1rem' }}>
                  用今日公开参考价统一换算国际黄金与国内上海金基准价，快速看清每克价差和溢价率。
                </p>

                <div className="market-gold-metrics">
                  <div className="market-card market-gold-metric">
                    <span>国际折算价</span>
                    <strong>{hasInternationalGold ? goldInternational.toFixed(2) : '--'}</strong>
                    <em>{hasInternationalGold ? '元/克' : '数据源暂不可用'}</em>
                  </div>
                  <div className="market-card market-gold-metric">
                    <span>国内参考金</span>
                    <strong>{fmtGoldGram(goldDomestic)}</strong>
                    <em>元/克</em>
                  </div>
                  <div className="market-card market-gold-metric">
                    <span>国内溢价</span>
                    <strong className={goldPremium >= 0 ? 'up' : 'down'}>{hasDomesticGold && hasInternationalGold ? `${goldPremium >= 0 ? '+' : ''}${goldPremium.toFixed(2)}` : '--'}</strong>
                    <em className={goldPremiumPct >= 0 ? 'up' : 'down'}>{hasDomesticGold && hasInternationalGold ? pct(goldPremiumPct) : '等待国内价'}</em>
                  </div>
                </div>

                <div className="market-gold-converter-grid" style={{ marginTop: '1rem' }}>
                  <div className="market-gold-amount">
                    <label htmlFor="market-gold-grams">克数 g</label>
                    <input
                      id="market-gold-grams"
                      type="number"
                      min="0"
                      step="0.1"
                      value={goldGrams}
                      onChange={(event) => setGoldGrams(event.target.value)}
                    />
                    <div className="market-gold-quick">
                      {[1, 10, 50, 100, 1000].map((grams) => (
                        <button type="button" key={grams} onClick={() => setGoldGrams(String(grams))}>{grams}g</button>
                      ))}
                    </div>
                  </div>
                  <div className="market-gold-totals">
                    <div>
                      <span>国际折算总价</span>
                      <strong>{fmtCny(goldInternationalTotal)}</strong>
                    </div>
                    <div>
                      <span>国内参考总价</span>
                      <strong>{fmtCny(goldDomesticTotal)}</strong>
                    </div>
                    <div>
                      <span>总价差</span>
                      <strong className={goldSpreadTotal >= 0 ? 'up' : 'down'}>{hasDomesticGold && hasInternationalGold && goldSpreadTotal >= 0 ? '+' : ''}{hasDomesticGold && hasInternationalGold ? fmtCny(goldSpreadTotal) : '--'}</strong>
                    </div>
                  </div>
                </div>

                <p className="market-gold-note">
                  数据来源：Yahoo Finance chart（GC=F + USDCNH=X）与上海黄金交易所首页上海金早/午盘价。
                  本工具用于市场换算参考，实际成交价格以交易平台和线下门店报价为准。
                </p>
              </section>
            </div>
          )}

          {activeTab === 'stock' && (
            <div className="market-panel" id="stock-overview" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem', opacity: 0.5 }}>📊</div>
              <h2 style={{ color: 'var(--market-ink)', fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>美股模块即将上线</h2>
              <p style={{ color: 'var(--market-soft)', fontSize: '0.85rem', maxWidth: '24rem', lineHeight: 1.7 }}>
                机构持仓（13F）、基本面研究、热门个股等功能正在开发中，敬请期待。
              </p>
            </div>
          )}

        </section>
      </div>

      <footer className="market-ticker" aria-label="市场滚动条">
        {marketRows.filter((coin) => coin.id !== 'pax-gold').slice(0, 5).map((coin) => (
          <button type="button" key={coin.id} onClick={() => navigate(`/crypto/${coin.id}`)}>
            {coin.symbol.toUpperCase()} {fmtUsd(coin.current_price, coin.current_price < 1 ? 4 : 0)}
            <span className={coin.change >= 0 ? 'up' : 'down'}>{pct(coin.change)}</span>
          </button>
        ))}
        <button type="button" onClick={() => navigate('/crypto/pax-gold')}>
          GOLD {fmtUsd(gold.current_price, 0)} <span className={gold.price_change_percentage_24h >= 0 ? 'up' : 'down'}>{pct(gold.price_change_percentage_24h)}</span>
        </button>
        {stockQuoteSymbols.slice(0, 2).map((symbol) => {
          const quote = stockQuotes[symbol];
          return (
            <button type="button" key={symbol} onClick={() => navigate(`/stock/${symbol}`)}>
              {symbol} {quote?.regularMarketPrice ? fmtUsd(quote.regularMarketPrice, 2) : '--'}
              <span className={(quote?.regularMarketChangePercent || 0) >= 0 ? 'up' : 'down'}>{pct(quote?.regularMarketChangePercent)}</span>
            </button>
          );
        })}
      </footer>
    </main>
  );
}
